// Google Analytics utility functions
export const GA_MEASUREMENT_ID = 'G-ZNF1BN68Y9';

// Declare gtag type for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: any
    ) => void;
  }
}

/**
 * Initialize Google Analytics
 * Call this after user consents to cookies
 * Deferred to not compete with image loading
 */
export const initGA = () => {
  if (typeof window === 'undefined') return;

  // Wait for page to be fully loaded before loading GA
  // This ensures images get priority for bandwidth
  if (document.readyState === 'complete') {
    loadGA();
  } else {
    // Wait for window load event (all resources loaded)
    window.addEventListener('load', loadGA, { once: true });
  }
};

/**
 * Actually load Google Analytics script
 * Called after page is fully loaded
 */
const loadGA = () => {
  // Initialize dataLayer first
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function before loading script
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  
  // Store gtag function globally before script loads
  window.gtag = gtag;

  // Load gtag.js script with low priority
  const script1 = document.createElement('script');
  script1.async = true;
  script1.defer = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  
  // Wait for script to load before configuring
  script1.onload = () => {
    window.gtag('js', new Date());
    // Avoid sending an automatic page_view here.
    // We send page views explicitly via `trackPageView()` once consent is confirmed.
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
    });
  };
  
  // Add to end of head to not block rendering
  document.head.appendChild(script1);
};

/**
 * Track a page view
 * @param path - The page path to track
 */
export const trackPageView = (path: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
  });
};

/**
 * Track a custom event
 * @param action - The action being tracked (e.g., 'click', 'submit')
 * @param category - The category of the event (e.g., 'button', 'form')
 * @param label - Optional label for the event
 * @param value - Optional numeric value
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

const CONSENT_COOKIE_NAME = 'waveOfInnovationConsent';

/**
 * Read consent from cookie (react-cookie-consent stores consent in a cookie, not localStorage)
 */
const getConsentFromCookie = (): boolean => {
  if (typeof document === 'undefined') return false;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + CONSENT_COOKIE_NAME + '=([^;]*)'));
  const value = match ? decodeURIComponent(match[1]) : null;
  return value === 'true';
};

/**
 * Check if user has consented to cookies.
 * Consent is stored in a COOKIE by react-cookie-consent; we must read that, not localStorage.
 */
export const hasConsented = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Primary: read cookie (where the consent library actually stores it)
  if (getConsentFromCookie()) return true;
  // Fallback: localStorage in case we ever set it on accept
  return localStorage.getItem(CONSENT_COOKIE_NAME) === 'true';
};
